import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { type ItemRow, type ItemListPage, type AppBootstrapPayload, emptyUploadRow } from '~~/shared/items/itemRow'
import type { FolderInfo } from '~~/shared/items/folderInfo'
import { pruneIds, type EntityCache } from '~/stores/entityCache'
import { PAGE_SIZE, type ApiFetch, type ListingParams, type LoadState } from '~/composables/documents/browser'

export interface FolderListingDeps {
  apiFetch: ApiFetch
  cache: EntityCache<ItemRow>
  listingParams: ComputedRef<ListingParams>
  trackPending: (rows: ItemRow[]) => void
  /** The indexing store's in-flight uploads for a path — rendered on top of the listing. */
  pendingUploadsIn: (path: string) => Array<{ id: string; name: string; path: string }>
  /** The folder tree's list and state: `bootstrap` fills both in the same request. */
  folders: Ref<FolderInfo[]>
  foldersState: Ref<LoadState>
}

/**
 * The flat listing of one folder — the finder's rows and the sidebar's folder mode (MAR-93, #469).
 * Split out of the documents store (MAR-948), which returns all of it under the same names.
 */
export function useFolderListing(deps: FolderListingDeps) {
  const { apiFetch, cache, listingParams, trackPending, pendingUploadsIn, folders, foldersState } = deps
  const folderIds = ref<string[]>([])
  const currentPath = ref('/')
  const loading = ref(false)
  const loadingMore = ref(false)
  /** How many items the current folder holds in total, vs. how many are loaded (MAR-93). */
  const folderTotal = ref(0)
  /**
   * THE SAME QUESTION `searchState` ANSWERS, ASKED OF THE LISTING (AlexAmin/marcus#469).
   *
   * Seven catches in this store carried `// tracked by useApi`, and the comment was false for all of
   * them: `app/plugins/api.ts`'s `onResponseError` acts on a 503, an invalid org override and the
   * 429 usage limit, and re-throws everything else untouched. A 500 was discarded and left the store
   * byte-identical to a successful EMPTY response — so the finder said `common.noItems`. One state
   * per SURFACE (this, the folder tree, the grouped seed, one folder's children), because the
   * surfaces fail independently and each has its own empty sentence to displace.
   */
  const listState = ref<LoadState>('idle')
  const listFailed = computed(() => listState.value === 'failed')
  /**
   * WHICH PATH `folderIds` ACTUALLY DESCRIBES, which is not always `currentPath`.
   *
   * `navigateTo` assigns `currentPath` BEFORE fetching — exactly as `searchItems` assigns
   * `searchQuery` before its request — so on a failed fetch the rows still on screen belong to the
   * folder the user just left. Rendering them under the new breadcrumb is a successful listing of
   * the wrong folder, with no signal at all; that half of #440 was worse than the "no results"
   * half, and it is the half that repeats here. Comparing against this tells the two cases apart:
   * a failed NAVIGATION has to drop its rows, a failed background REFETCH of the folder already on
   * screen must not, because those rows are genuinely this path's.
   */
  const loadedPath = ref<string | null>(null)

  /** The flat library listing for the current path, with in-flight uploads on top. */
  const items = computed<ItemRow[]>(() => {
    const uploads = pendingUploadsIn(currentPath.value)
      .map(job => emptyUploadRow({ id: job.id, name: job.name, path: job.path }))
    return [...uploads, ...cache.resolve(folderIds.value)]
  })
  /**
   * The server rows for the current folder, WITHOUT the optimistic upload placeholders `items`
   * prepends. Callers asking "does anything exist here" need this: an in-flight upload is not yet a
   * document, and counting it flips empty-state gates a beat early.
   */
  const folderRows = computed<ItemRow[]>(() => cache.resolve(folderIds.value))
  /** Whether the current folder has rows beyond the ones already loaded. */
  const hasMoreItems = computed(() => folderIds.value.length < folderTotal.value)

  const breadcrumb = computed(() => {
    if (currentPath.value === '/') return []
    const segments = currentPath.value.split('/').filter(Boolean)
    return segments.map((seg, i) => ({ name: seg, path: '/' + segments.slice(0, i + 1).join('/') }))
  })

  /**
   * One page of the current folder. `append` is the "load more" path (MAR-93).
   *
   * Passing `limit` opts into the `{ items, total }` envelope; the store always pages, so the finder
   * can show an honest "showing X of Y" instead of a silently truncated list. A refetch (append
   * false) deliberately re-requests everything already on screen — `offset: 0, limit: loaded` — so a
   * settling upload cannot make page 2 vanish from under the user.
   */
  async function fetchItems(options: { append?: boolean } = {}) {
    const append = options.append ?? false
    if (folderIds.value.length === 0) loading.value = true
    if (append) loadingMore.value = true
    // Captured BEFORE the await, because `navigateTo` may move `currentPath` again while this
    // request is in flight; the answer belongs to the path that was asked for.
    const requestedPath = currentPath.value
    listState.value = 'loading'
    try {
      const offset = append ? folderIds.value.length : 0
      const limit = append ? PAGE_SIZE : Math.max(PAGE_SIZE, folderIds.value.length)
      const { items: rows, total } = await apiFetch<ItemListPage>('/api/items', {
        params: { path: currentPath.value, limit, offset, ...listingParams.value },
      })
      cache.upsert(rows)
      const ids = rows.map(r => r.id)
      // Dedupe on append: a row inserted above the cursor between the two requests would otherwise
      // arrive twice and render twice.
      folderIds.value = append ? [...folderIds.value, ...ids.filter(id => !folderIds.value.includes(id))] : ids
      folderTotal.value = total
      loadedPath.value = requestedPath
      listState.value = 'ready'
      trackPending(rows)
    } catch (err) {
      // "Your library is empty" is a claim about the organization's documents; a failed request
      // knows nothing about them (AlexAmin/marcus#469). Named, so a trace says WHICH request died.
      listState.value = 'failed'
      console.error('[documents] /api/items failed', err)

      // THE ROWS GO ONLY WHEN THEY ARE ANOTHER FOLDER'S (see `loadedPath`). A failed background
      // REFETCH of the folder already on screen keeps its rows: the indexing poller refetches the
      // current folder on every settled job, and blanking a correct library to announce that a
      // background refresh failed would be worse than the failure. A failed `append` keeps its page
      // for the same reason — the widening is what broke, not the rows already loaded.
      if (!append && loadedPath.value !== requestedPath) {
        folderIds.value = []
        folderTotal.value = 0
      }
    } finally {
      loading.value = false
      loadingMore.value = false
    }
  }

  /** Pull the next page of the current folder. No-op once everything is on screen. */
  async function loadMoreItems() {
    if (loadingMore.value || !hasMoreItems.value) return
    await fetchItems({ append: true })
  }

  /** One-shot mount load: items + folders in one request, so the /app shell pays one auth resolution. */
  async function bootstrap() {
    if (folderIds.value.length === 0) loading.value = true
    const requestedPath = currentPath.value
    listState.value = 'loading'
    foldersState.value = 'loading'
    try {
      // The lens rides here too (MAR-957). This runs whenever `PanelLayout` mounts — not only on
      // the first load: the shell unmounts on any non-`/app` route and this singleton keeps its
      // filter, so coming back used to repaint the listing unfiltered under an active header.
      const { items: rows, folders: folderList } = await apiFetch<AppBootstrapPayload>(
        '/api/app/bootstrap', { params: { path: currentPath.value, ...listingParams.value } },
      )
      cache.upsert(rows)
      folderIds.value = rows.map(r => r.id)
      folders.value = folderList
      loadedPath.value = requestedPath
      listState.value = 'ready'
      foldersState.value = 'ready'
      trackPending(rows)
    } catch (err) {
      // The FIRST PAINT OF THE APP (AlexAmin/marcus#469): a 500 here used to mean a user's first
      // sight of Marcus was an empty library with a "Subir" button, indistinguishable from a
      // brand-new account. No rows are dropped: nothing is on screen yet that could belong to
      // another path. `fetchItems` owns that case.
      listState.value = 'failed'
      foldersState.value = 'failed'
      console.error('[documents] /api/app/bootstrap failed', err)
    } finally {
      loading.value = false
    }
  }


  /** Drop removed documents from the listing. Part of the store's `remove()`. */
  function prune(gone: Set<string>) {
    folderIds.value = pruneIds(folderIds.value, gone)
  }

  return {
    currentPath, loading, loadingMore, folderTotal, listState, listFailed,
    items, folderRows, hasMoreItems, breadcrumb,
    fetchItems, loadMoreItems, bootstrap, prune,
  }
}
